import {
  DEFAULT_SPATIAL_SUBSPACE,
  MAX_SPATIAL_SUBSPACES,
  addSpatialSubspaceRequestSchema,
  type ServerSummary,
  type SpatialSubspacesResponse,
} from "@woven/contracts";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "../../auth/context";
import { ControlApiError } from "../../lib/api";
import { useControlApiClient } from "../../lib/hooks";

interface SpatialForm {
  slug: string;
  displayName: string;
  metersPerUnit: string;
  cellSize: string;
  interestRadius: string;
  exactDistance: boolean;
  minX: string;
  minY: string;
  minZ: string;
  maxX: string;
  maxY: string;
  maxZ: string;
}

const initialForm = (): SpatialForm => ({
  slug: "",
  displayName: "",
  metersPerUnit: String(DEFAULT_SPATIAL_SUBSPACE.metersPerUnit),
  cellSize: String(DEFAULT_SPATIAL_SUBSPACE.cellSize),
  interestRadius: String(DEFAULT_SPATIAL_SUBSPACE.interestRadius),
  exactDistance: DEFAULT_SPATIAL_SUBSPACE.exactDistance,
  minX: String(DEFAULT_SPATIAL_SUBSPACE.bounds.min.x),
  minY: String(DEFAULT_SPATIAL_SUBSPACE.bounds.min.y),
  minZ: String(DEFAULT_SPATIAL_SUBSPACE.bounds.min.z),
  maxX: String(DEFAULT_SPATIAL_SUBSPACE.bounds.max.x),
  maxY: String(DEFAULT_SPATIAL_SUBSPACE.bounds.max.y),
  maxZ: String(DEFAULT_SPATIAL_SUBSPACE.bounds.max.z),
});

export function SpatialSubspaces({ server }: { server: ServerSummary }) {
  const client = useControlApiClient();
  const { user } = useAuth();
  const scope = useMemo(
    () => ({
      client,
      ownerUid: user?.uid,
      id: server.id,
      generation: server.generation,
      status: server.status,
      deleteIntent: server.lifecycle?.deleteIntent === true,
    }),
    [client, user?.uid, server.id, server.generation, server.status, server.lifecycle?.deleteIntent]
  );
  const sessionRef = useRef<{ scope: typeof scope; controller: AbortController | null } | null>(
    null
  );
  const [snapshot, setSnapshot] = useState<{
    scope: typeof scope;
    data?: SpatialSubspacesResponse;
    busy?: "load" | "add";
    error?: string;
  } | null>(null);
  const [dialog, setDialog] = useState<{ scope: typeof scope; form: SpatialForm } | null>(null);
  const current = snapshot?.scope === scope ? snapshot : null;
  const dialogOpen = dialog?.scope === scope;
  const canManage =
    !!scope.ownerUid && !!scope.generation && scope.status === "running" && !scope.deleteIntent;
  const spatialSpaces =
    current?.data?.spaces.filter((space) => space.kind === "spatialGrid3D") ?? [];
  const full = spatialSpaces.length >= MAX_SPATIAL_SUBSPACES;

  const load = useCallback(
    async (session = sessionRef.current) => {
      if (
        !session ||
        session.scope !== scope ||
        session.controller ||
        !scope.generation ||
        !canManage
      )
        return;
      const controller = new AbortController();
      session.controller = controller;
      setSnapshot((value) => ({
        scope,
        data: value?.scope === scope ? value.data : undefined,
        busy: "load",
      }));
      const active = () => sessionRef.current === session && !controller.signal.aborted;
      try {
        const data = await client.listServerSubspaces(
          scope.id,
          { generation: scope.generation },
          controller.signal
        );
        if (!active()) return;
        if (data.generation !== scope.generation) {
          setSnapshot({
            scope,
            error: "Server generation changed. Refresh server status before retrying.",
          });
        } else {
          setSnapshot({ scope, data });
        }
      } catch (error) {
        if (active()) setSnapshot({ scope, error: operationMessage(error) });
      } finally {
        if (session.controller === controller) session.controller = null;
      }
    },
    [canManage, client, scope]
  );

  useLayoutEffect(() => {
    const session = { scope, controller: null as AbortController | null };
    sessionRef.current = session;
    if (canManage) queueMicrotask(() => void load(session));
    return () => {
      sessionRef.current = null;
      session.controller?.abort();
      setSnapshot((value) => (value?.scope === scope ? null : value));
      setDialog((value) => (value?.scope === scope ? null : value));
    };
  }, [scope, canManage, load]);

  useEffect(() => {
    if (!dialogOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape" && current?.busy !== "add") setDialog(null);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [dialogOpen, current?.busy]);

  function updateForm<K extends keyof SpatialForm>(field: K, nextValue: SpatialForm[K]) {
    setDialog((value) =>
      value?.scope === scope ? { ...value, form: { ...value.form, [field]: nextValue } } : value
    );
  }

  async function add() {
    const session = sessionRef.current;
    if (
      !session ||
      session.scope !== scope ||
      session.controller ||
      !dialogOpen ||
      !scope.generation ||
      !canManage ||
      full
    )
      return;
    const form = dialog.form;
    const numericValues = [
      form.metersPerUnit,
      form.cellSize,
      form.interestRadius,
      form.minX,
      form.minY,
      form.minZ,
      form.maxX,
      form.maxY,
      form.maxZ,
    ];
    if (numericValues.some((value) => value.trim() === "")) {
      setSnapshot({
        scope,
        data: current?.data,
        error: "All spatial configuration and AABB values are required.",
      });
      return;
    }
    const parsed = addSpatialSubspaceRequestSchema.safeParse({
      generation: scope.generation,
      slug: form.slug,
      displayName: form.displayName,
      metersPerUnit: Number(form.metersPerUnit),
      cellSize: Number(form.cellSize),
      interestRadius: Number(form.interestRadius),
      exactDistance: form.exactDistance,
      bounds: {
        min: { x: Number(form.minX), y: Number(form.minY), z: Number(form.minZ) },
        max: { x: Number(form.maxX), y: Number(form.maxY), z: Number(form.maxZ) },
      },
    });
    if (!parsed.success) {
      setSnapshot({
        scope,
        data: current?.data,
        error: parsed.error.issues[0]?.message ?? "Invalid spatial subspace definition.",
      });
      return;
    }
    const controller = new AbortController();
    session.controller = controller;
    setSnapshot({ scope, data: current?.data, busy: "add" });
    const active = () => sessionRef.current === session && !controller.signal.aborted;
    try {
      const data = await client.addServerSubspace(scope.id, parsed.data, controller.signal);
      if (!active()) return;
      if (data.generation !== scope.generation) {
        setSnapshot({
          scope,
          error: "Server generation changed. Refresh server status before retrying.",
        });
      } else {
        setSnapshot({ scope, data });
        setDialog(null);
      }
    } catch (error) {
      if (active()) setSnapshot({ scope, data: current?.data, error: operationMessage(error) });
    } finally {
      if (session.controller === controller) session.controller = null;
    }
  }

  function closeDialog() {
    if (current?.busy !== "add") setDialog(null);
  }

  return (
    <>
      <section className="panel spatial-subspaces" aria-labelledby="spatial-subspaces-title">
        <div className="spatial-subspaces-head">
          <div>
            <h2 className="panel-title" id="spatial-subspaces-title">
              Spatial Subspaces
            </h2>
            <p className="form-note">
              Managed 3D routing spaces. Definitions are add-only for this server generation.
            </p>
          </div>
          <div className="spatial-subspaces-actions">
            <span className="cell-mono" aria-label="Spatial subspace count">
              {spatialSpaces.length} / {MAX_SPATIAL_SUBSPACES}
            </span>
            <button
              type="button"
              className="btn btn--primary"
              disabled={!canManage || !current?.data || !!current.busy || full}
              onClick={() => {
                setSnapshot((value) =>
                  value?.scope === scope ? { ...value, error: undefined } : { scope }
                );
                setDialog({ scope, form: initialForm() });
              }}
            >
              Add Spatial Subspace
            </button>
          </div>
        </div>

        {!canManage && (
          <p className="form-note">
            Spatial definitions are available only while this managed server generation is running
            and not being deleted.
          </p>
        )}
        {canManage && current?.busy === "load" && !current.data && <p>Loading definitions…</p>}
        {canManage && current?.data && spatialSpaces.length === 0 && (
          <p className="form-note">No user-configured spatial subspaces yet.</p>
        )}
        {spatialSpaces.length > 0 && (
          <div className="spatial-subspaces-grid">
            {spatialSpaces.map((space) => (
              <article className="spatial-subspace-card" key={space.spaceId}>
                <div className="spatial-subspace-card-head">
                  <div>
                    <h3>{space.displayName}</h3>
                    <p className="cell-mono">{space.slug}</p>
                  </div>
                  <span className="badge badge--ok">Space {space.spaceId}</span>
                </div>
                <dl className="detail-list">
                  <div className="detail-row">
                    <dt>Scale / cell / radius</dt>
                    <dd className="cell-mono">
                      {space.metersPerUnit} m/unit · {space.cellSize} · {space.interestRadius}
                    </dd>
                  </div>
                  <div className="detail-row">
                    <dt>Bounds min</dt>
                    <dd className="cell-mono">{point(space.bounds.min)}</dd>
                  </div>
                  <div className="detail-row">
                    <dt>Bounds max</dt>
                    <dd className="cell-mono">{point(space.bounds.max)}</dd>
                  </div>
                  <div className="detail-row">
                    <dt>Exact distance</dt>
                    <dd>{space.exactDistance ? "Enabled" : "Disabled"}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        )}
        {current?.error && <p role="alert">{current.error}</p>}
        {canManage && current?.error && (
          <button className="btn btn--ghost" disabled={!!current.busy} onClick={() => void load()}>
            Retry Definitions
          </button>
        )}
      </section>

      {dialogOpen && (
        <div className="modal-backdrop" onClick={closeDialog}>
          <section
            className="modal spatial-subspace-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-spatial-subspace-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h2 className="modal-title" id="add-spatial-subspace-title">
                Add Spatial Subspace
              </h2>
              <button
                className="btn btn--ghost btn--sm"
                disabled={current?.busy === "add"}
                onClick={closeDialog}
              >
                Close
              </button>
            </div>
            <p className="form-note">
              Slugs and definitions are immutable. An exact retry is idempotent; a conflicting slug
              is rejected.
            </p>
            <div className="form spatial-subspace-form">
              <div className="form-row">
                <Field label="Stable slug">
                  <input
                    className="input"
                    value={dialog.form.slug}
                    minLength={1}
                    maxLength={64}
                    pattern="[a-z0-9][a-z0-9-]{0,63}"
                    autoComplete="off"
                    autoFocus
                    disabled={current?.busy === "add"}
                    onChange={(event) => updateForm("slug", event.target.value)}
                  />
                </Field>
                <Field label="Display name">
                  <input
                    className="input"
                    value={dialog.form.displayName}
                    minLength={1}
                    maxLength={64}
                    autoComplete="off"
                    disabled={current?.busy === "add"}
                    onChange={(event) => updateForm("displayName", event.target.value)}
                  />
                </Field>
              </div>
              <div className="spatial-number-grid">
                <NumberField
                  label="Meters per unit"
                  value={dialog.form.metersPerUnit}
                  disabled={current?.busy === "add"}
                  onChange={(value) => updateForm("metersPerUnit", value)}
                />
                <NumberField
                  label="Cell size"
                  value={dialog.form.cellSize}
                  disabled={current?.busy === "add"}
                  onChange={(value) => updateForm("cellSize", value)}
                />
                <NumberField
                  label="Interest radius"
                  value={dialog.form.interestRadius}
                  disabled={current?.busy === "add"}
                  onChange={(value) => updateForm("interestRadius", value)}
                />
              </div>
              <fieldset className="spatial-bounds-fieldset">
                <legend className="field-label">AABB bounds</legend>
                <div className="spatial-number-grid">
                  {(["minX", "minY", "minZ", "maxX", "maxY", "maxZ"] as const).map((field) => (
                    <NumberField
                      key={field}
                      label={`${field.startsWith("min") ? "Min" : "Max"} ${field.at(-1)!.toUpperCase()}`}
                      value={dialog.form[field]}
                      disabled={current?.busy === "add"}
                      onChange={(value) => updateForm(field, value)}
                    />
                  ))}
                </div>
              </fieldset>
              <label className="spatial-checkbox">
                <input
                  type="checkbox"
                  checked={dialog.form.exactDistance}
                  disabled={current?.busy === "add"}
                  onChange={(event) => updateForm("exactDistance", event.target.checked)}
                />
                Use exact 3D distance after cell routing
              </label>
              {current?.error && <p role="alert">{current.error}</p>}
              <div className="server-actions">
                <button
                  className="btn btn--primary"
                  disabled={current?.busy === "add"}
                  onClick={() => void add()}
                >
                  {current?.busy === "add" ? "Adding…" : "Add Spatial Subspace"}
                </button>
                <button
                  className="btn btn--ghost"
                  disabled={current?.busy === "add"}
                  onClick={closeDialog}
                >
                  Cancel
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

function NumberField({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label}>
      <input
        className="input"
        type="number"
        step="any"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

function point(value: { x: number; y: number; z: number }): string {
  return `(${value.x}, ${value.y}, ${value.z})`;
}

function operationMessage(error: unknown): string {
  if (error instanceof ControlApiError) {
    if (error.code === "CONFLICT")
      return "The slug, limit, generation, or mutation state conflicts with the current server. Refresh definitions before retrying.";
    if (error.code === "UNAVAILABLE")
      return "Spatial definitions could not be confirmed with the managed Woven node. Retry to reconcile the saved mutation plan.";
  }
  return "Spatial subspace operation was not confirmed. Retry to reconcile before submitting another definition.";
}
